import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import {
  BarChart3, Users, MousePointerClick, Eye, Activity, Loader2, Globe, Monitor,
  Smartphone, Chrome, ArrowUpRight, ShieldCheck,
} from "lucide-react";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";

const ease = [0.16, 1, 0.3, 1];
const RANGES = [{ k: "24h", l: "24 h" }, { k: "7d", l: "7 días" }, { k: "14d", l: "14 días" }, { k: "30d", l: "30 días" }, { k: "90d", l: "90 días" }];
const SCOPES = [{ k: "all", l: "Todo" }, { k: "public", l: "Sitio público" }, { k: "app", l: "Dentro de OMNIA" }];

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-navy/40 text-xs uppercase tracking-wide"><Icon className="w-4 h-4" />{label}</div>
    <div className="text-3xl font-semibold text-navy mt-2">{value}</div>
  </div>
);

function Breakdown({ title, icon: Icon, data, testid }) {
  const max = Math.max(1, ...(data || []).map((d) => d.count));
  return (
    <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-navy/70 font-medium mb-4"><Icon className="w-4 h-4 text-violet" />{title}</div>
      {(!data || data.length === 0) ? <p className="text-sm text-navy/40 py-4">Sin datos aún.</p> : (
        <div className="space-y-2.5">
          {data.map((d) => (
            <div key={d.value} className="relative">
              <div className="flex justify-between text-sm relative z-10 px-2 py-1">
                <span className="text-navy/80 truncate max-w-[70%]">{d.value}</span>
                <span className="text-navy/50 tabular-nums">{d.count.toLocaleString("es-MX")}</span>
              </div>
              <div className="absolute inset-0 bg-violet/10 rounded-md" style={{ width: `${Math.max(6, (d.count / max) * 100)}%` }} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function OmniaAnalytics() {
  const [range, setRange] = useState("7d");
  const [scope, setScope] = useState("all");
  const [ov, setOv] = useState(null);
  const [series, setSeries] = useState([]);
  const [bd, setBd] = useState({});
  const [realtime, setRealtime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const q = `range=${range}&scope=${scope}`;
    const dims = ["path", "referrer_domain", "country", "device_type", "browser", "os", "role", "gender", "age_bucket", "event"];
    Promise.all([
      api.get(`/analytics/overview?${q}`),
      api.get(`/analytics/timeseries?${q}`),
      api.get(`/analytics/realtime`),
      ...dims.map((d) => api.get(`/analytics/breakdown?dim=${d}&${q}`)),
    ]).then((res) => {
      setOv(res[0].data);
      setSeries(res[1].data.map((r) => ({ ...r, label: r.day.slice(5) })));
      setRealtime(res[2].data.active_visitors);
      const b = {};
      dims.forEach((d, i) => { b[d] = res[3 + i].data; });
      setBd(b);
    }).catch((e) => { if (e.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, [range, scope]);
  useEffect(() => { load(); }, [load]);

  if (denied) {
    return (
      <OSLayout>
        <div className="max-w-lg mx-auto mt-24 text-center" data-testid="analytics-denied">
          <ShieldCheck className="w-12 h-12 text-violet mx-auto mb-4" />
          <h1 className="text-2xl font-semibold text-navy">Acceso restringido</h1>
          <p className="text-navy/60 mt-2 text-sm">Solo el liderazgo o admin puede ver la analítica.</p>
        </div>
      </OSLayout>
    );
  }

  return (
    <OSLayout>
      <div className="max-w-6xl mx-auto pb-24">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-violet text-xs font-medium mb-2"><BarChart3 className="w-4 h-4" /> OMNIA ANALYTICS</div>
            <h1 className="text-3xl sm:text-4xl font-semibold text-navy tracking-tight">Analítica</h1>
            <p className="text-navy/60 mt-2 text-sm max-w-2xl">Tráfico real de tu sitio público y del uso interno de OMNIA. Los bots y el tráfico de pruebas se excluyen automáticamente.</p>
          </div>
          <div className="flex items-center gap-2 rounded-full bg-emerald-500/10 text-emerald-600 px-4 py-2 text-sm" data-testid="analytics-realtime">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" /></span>
            {realtime} activos ahora
          </div>
        </div>

        {/* filtros */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <div className="flex gap-1 bg-navy/5 rounded-full p-1" data-testid="analytics-range">
            {RANGES.map((r) => (
              <button key={r.k} onClick={() => setRange(r.k)} data-testid={`range-${r.k}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${range === r.k ? "bg-white text-navy shadow-sm" : "text-navy/50 hover:text-navy"}`}>{r.l}</button>
            ))}
          </div>
          <div className="flex gap-1 bg-navy/5 rounded-full p-1" data-testid="analytics-scope">
            {SCOPES.map((s) => (
              <button key={s.k} onClick={() => setScope(s.k)} data-testid={`scope-${s.k}`}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${scope === s.k ? "bg-white text-navy shadow-sm" : "text-navy/50 hover:text-navy"}`}>{s.l}</button>
            ))}
          </div>
        </div>

        {loading || !ov ? (
          <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-violet" /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ ease }} className="space-y-6">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Kpi icon={Users} label="Visitantes" value={ov.visitors.toLocaleString("es-MX")} testid="kpi-visitors" />
              <Kpi icon={Eye} label="Vistas" value={ov.pageviews.toLocaleString("es-MX")} testid="kpi-pageviews" />
              <Kpi icon={Activity} label="Sesiones" value={ov.sessions.toLocaleString("es-MX")} testid="kpi-sessions" />
              <Kpi icon={MousePointerClick} label="Eventos" value={ov.events.toLocaleString("es-MX")} testid="kpi-events" />
              <Kpi icon={ArrowUpRight} label="Rebote" value={`${ov.bounce_rate}%`} testid="kpi-bounce" />
            </div>

            {/* gráfica de tráfico */}
            <div className="rounded-2xl border border-navy/10 bg-white p-5" data-testid="analytics-chart">
              <div className="text-navy/70 font-medium mb-4">Tráfico a lo largo del tiempo</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <AreaChart data={series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#7C5CFC" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#7C5CFC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#0000000d" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#8888" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#8888" }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #eee", fontSize: 13 }} />
                    <Area type="monotone" dataKey="visits" name="Vistas" stroke="#7C5CFC" strokeWidth={2.5} fill="url(#g)" />
                    <Area type="monotone" dataKey="visitors" name="Visitantes" stroke="#22c55e" strokeWidth={2} fillOpacity={0} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* desgloses */}
            <div className="grid md:grid-cols-2 gap-4">
              <Breakdown title="Páginas más vistas" icon={Eye} data={bd.path} testid="bd-path" />
              <Breakdown title="De dónde llegan (referentes)" icon={Globe} data={bd.referrer_domain} testid="bd-referrer" />
              <Breakdown title="Países" icon={Globe} data={bd.country} testid="bd-country" />
              <Breakdown title="Dispositivos" icon={Smartphone} data={bd.device_type} testid="bd-device" />
              <Breakdown title="Navegadores" icon={Chrome} data={bd.browser} testid="bd-browser" />
              <Breakdown title="Sistemas operativos" icon={Monitor} data={bd.os} testid="bd-os" />
            </div>

            {/* demografía y uso interno */}
            <div className="text-navy/70 font-medium pt-2">Usuarios de OMNIA (logueados)</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Breakdown title="Por rol" icon={Users} data={bd.role} testid="bd-role" />
              <Breakdown title="Por género" icon={Users} data={bd.gender} testid="bd-gender" />
              <Breakdown title="Por edad" icon={Users} data={bd.age_bucket} testid="bd-age" />
              <Breakdown title="Eventos / acciones" icon={MousePointerClick} data={bd.event} testid="bd-event" />
            </div>
            <p className="text-xs text-navy/40">La demografía (edad/género) solo aplica a usuarios que iniciaron sesión y completaron su perfil; los visitantes anónimos del sitio público no la tienen.</p>
          </motion.div>
        )}
      </div>
    </OSLayout>
  );
}
